import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Organization,
  Reminder,
  ReminderAttachment,
  ReminderRepeat,
} from '@nargus/shared';
import { Link } from '@tanstack/react-router';
import {
  BellRing,
  Check,
  LoaderCircle,
  Paperclip,
  Pencil,
  Plus,
  Repeat,
  RotateCcw,
  Trash2,
  Upload,
  Users,
} from 'lucide-react';
import { useSession } from '@/resources/auth/auth.service';
import { useStaffMembers } from '@/resources/organization/organization.service';
import {
  attachmentLoader,
  useAddAttachment,
  useCreateReminder,
  useDeleteReminder,
  useNudgeReminder,
  useReminders,
  useRemoveAttachment,
  useUpdateReminder,
} from '@/resources/reminder/reminder.service';
import { MAX_FILE_BYTES } from '@/lib/files';
import { FileGallery } from '@/components/file-gallery';
import { UserAvatar } from '@/components/user-avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog, EmptyNote, ErrorNote, SectionHeader } from './ui';

const WHEN = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const NOBODY = '';

const ONCE = '';

const REPEATS: ReminderRepeat[] = ['daily', 'weekly', 'monthly', 'yearly'];

function useRepeatLabels(): Record<string, string> {
  const { t } = useTranslation();
  return {
    [ONCE]: t('reminders.repeatOnce'),
    daily: t('reminders.repeatDaily'),
    weekly: t('reminders.repeatWeekly'),
    monthly: t('reminders.repeatMonthly'),
    yearly: t('reminders.repeatYearly'),
  };
}

export function RemindersSection({ organization }: { organization: Organization }) {
  const { t } = useTranslation();
  const reminders = useReminders(organization.id);

  const open = reminders.data?.filter((r) => !r.doneAt) ?? [];
  const done = reminders.data?.filter((r) => r.doneAt) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <SectionHeader title={t('reminders.title')} hint={t('reminders.hint')} />
        <Button
          size="icon"
          className="shrink-0"
          aria-label={t('reminders.addShort')}
          render={
            <Link
              to="/organizations/$organizationId/reminders/new"
              params={{ organizationId: organization.id }}
            />
          }
        >
          <Plus />
        </Button>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>{t('reminders.openTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1">
          {reminders.isPending ? (
            <>
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </>
          ) : open.length === 0 ? (
            <EmptyNote>{t('reminders.none')}</EmptyNote>
          ) : (
            open.map((reminder) => (
              <Row key={reminder.id} reminder={reminder} organization={organization} />
            ))
          )}
          <ErrorNote error={reminders.error} />
        </CardContent>
      </Card>

      {done.length > 0 && (
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle>{t('reminders.doneTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1">
            {done.map((reminder) => (
              <Row key={reminder.id} reminder={reminder} organization={organization} />
            ))}
          </CardContent>
        </Card>
      )}

    </div>
  );
}

function useDueLabel(reminder: Reminder): { text: string; overdue: boolean } {
  const { t } = useTranslation();

  if (reminder.doneAt) {
    return {
      text: t('reminders.doneOn', { when: WHEN.format(new Date(reminder.doneAt)) }),
      overdue: false,
    };
  }
  if (!reminder.dueAt) {
    return { text: t('reminders.noDue'), overdue: false };
  }

  const due = new Date(reminder.dueAt);
  const overdue = due.getTime() < Date.now();
  return {
    text: overdue
      ? t('reminders.overdue', { when: WHEN.format(due) })
      : t('reminders.dueOn', { when: WHEN.format(due) }),
    overdue,
  };
}

function Row({
  reminder,
  organization,
}: {
  reminder: Reminder;
  organization: Organization;
}) {
  const { t } = useTranslation();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [nudging, setNudging] = useState(false);
  const update = useUpdateReminder();
  const remove = useDeleteReminder();
  const due = useDueLabel(reminder);
  const repeatLabels = useRepeatLabels();
  const isDone = Boolean(reminder.doneAt);
  const busy = update.isPending || remove.isPending;
  const assignee = reminder.assignee;

  return (
    <div className="flex items-start gap-3 rounded-lg py-2 hover:bg-muted/50">
      <Button
        variant={isDone ? 'ghost' : 'outline'}
        size="icon-sm"
        className="mt-0.5 shrink-0"
        aria-label={
          isDone
            ? t('reminders.reopen', { title: reminder.title })
            : t('reminders.complete', { title: reminder.title })
        }
        disabled={busy}
        onClick={() =>
          update.mutate({
            organizationId: organization.id,
            reminderId: reminder.id,
            done: !isDone,
          })
        }
      >
        {isDone ? <RotateCcw /> : <Check />}
      </Button>

      <div className="min-w-0 flex-1">
        <p
          className={`text-sm font-medium ${isDone ? 'text-muted-foreground line-through' : ''}`}
        >
          {reminder.title}
        </p>
        {reminder.note && <p className="text-sm text-muted-foreground">{reminder.note}</p>}

        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={`text-xs ${due.overdue ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {due.text}
          </span>
          {reminder.repeat && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Repeat className="size-3" />
              {repeatLabels[reminder.repeat]}
            </span>
          )}
          {assignee ? (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <UserAvatar
                size="sm"
                userId={assignee.userId}
                name={assignee.name}
                email={assignee.email}
                avatarUpdatedAt={assignee.avatarUpdatedAt}
              />
              {assignee.name ?? assignee.email}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="size-3" />
              {t('reminders.everyone')}
            </span>
          )}
          {reminder.attachments.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Paperclip className="size-3" />
              {reminder.attachments.length}
            </span>
          )}
        </div>
      </div>

      {!isDone && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="mt-0.5 shrink-0"
          aria-label={t('reminders.nudge', { title: reminder.title })}
          onClick={() => setNudging(true)}
        >
          <BellRing />
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        className="mt-0.5 shrink-0"
        aria-label={t('reminders.edit', { title: reminder.title })}
        // No `disabled` here: it is a link, and opening the form during a
        // pending toggle does no harm.
        render={
          <Link
            to="/organizations/$organizationId/reminders/$reminderId/edit"
            params={{ organizationId: organization.id, reminderId: reminder.id }}
          />
        }
      >
        <Pencil />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        className="mt-0.5 shrink-0"
        aria-label={t('reminders.remove', { title: reminder.title })}
        disabled={busy}
        onClick={() => setConfirmRemove(true)}
      >
        <Trash2 />
      </Button>

      {nudging && (
        <NudgeDialog
          organization={organization}
          reminder={reminder}
          onClose={() => setNudging(false)}
        />
      )}

      {confirmRemove && (
        <ConfirmDialog
          title={t('reminders.removeTitle')}
          description={t('reminders.removeHint', { title: reminder.title })}
          confirmLabel={t('common.delete')}
          busy={remove.isPending}
          error={remove.error}
          onClose={() => setConfirmRemove(false)}
          onConfirm={() =>
            remove.mutate({ organizationId: organization.id, reminderId: reminder.id })
          }
        />
      )}
    </div>
  );
}

function NudgeDialog({
  organization,
  reminder,
  onClose,
}: {
  organization: Organization;
  reminder: Reminder;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useSession();
  const members = useStaffMembers(organization.id);
  const nudge = useNudgeReminder();
  const [picked, setPicked] = useState('');

  const others = (members.data ?? []).filter((member) => member.userId !== user?.id);
  const labels = Object.fromEntries(
    others.map((member) => [member.userId, member.name ?? member.email]),
  );
  const assignee = reminder.assignee;
  const fallback =
    assignee && assignee.userId !== user?.id
      ? assignee.userId
      : (others[0]?.userId ?? NOBODY);
  const recipient = picked || fallback;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('reminders.nudgeTitle')}</DialogTitle>
          <DialogDescription>
            {t('reminders.nudgeHint', { title: reminder.title })}
          </DialogDescription>
        </DialogHeader>

        {nudge.isSuccess ? (
          <p className="text-sm text-muted-foreground">
            {t('reminders.nudgeSent', { name: labels[recipient] ?? '' })}
          </p>
        ) : members.isPending ? (
          <Skeleton className="h-9 w-full" />
        ) : others.length === 0 ? (
          <EmptyNote>{t('reminders.nudgeNobody')}</EmptyNote>
        ) : (
          <Field>
            <FieldLabel htmlFor="nudge-recipient">
              {t('reminders.nudgeRecipient')}
            </FieldLabel>
            <Select
              items={labels}
              value={recipient}
              onValueChange={(value) => setPicked(String(value ?? NOBODY))}
            >
              <SelectTrigger id="nudge-recipient" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {others.map((member) => (
                  <SelectItem key={member.userId} value={member.userId}>
                    {member.name ?? member.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}

        <ErrorNote error={nudge.error} />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={nudge.isPending}
          >
            {nudge.isSuccess ? t('common.close') : t('reminders.cancel')}
          </Button>
          {!nudge.isSuccess && (
            <Button
              type="button"
              disabled={!recipient || nudge.isPending}
              onClick={() =>
                nudge.mutate({
                  organizationId: organization.id,
                  reminderId: reminder.id,
                  userId: recipient,
                })
              }
            >
              {nudge.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <BellRing />
              )}
              {nudge.isPending ? t('reminders.nudgeSending') : t('reminders.nudgeSend')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Attachments({
  organization,
  reminder,
}: {
  organization: Organization;
  reminder: Reminder;
}) {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);
  const [tooLarge, setTooLarge] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<ReminderAttachment | null>(null);
  const add = useAddAttachment();
  const remove = useRemoveAttachment();
  const ref = { organizationId: organization.id, reminderId: reminder.id };
  const busy = add.isPending || remove.isPending;

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setTooLarge(file.size > MAX_FILE_BYTES);
    if (file.size > MAX_FILE_BYTES) return;
    add.mutate({ ...ref, file });
  }

  return (
    <Field>
      <FieldLabel htmlFor="reminder-file">{t('reminders.filesLabel')}</FieldLabel>

      <FileGallery
        files={reminder.attachments}
        load={attachmentLoader(ref)}
        disabled={busy}
        onRemove={setConfirmRemove}
      />

      <input id="reminder-file" ref={picker} type="file" className="hidden" onChange={onPick} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={busy}
        onClick={() => picker.current?.click()}
      >
        {add.isPending ? <LoaderCircle className="size-4 animate-spin" /> : <Upload />}
        {add.isPending ? t('reminders.fileUploading') : t('reminders.fileAdd')}
      </Button>

      <FieldDescription>{t('reminders.filesHint')}</FieldDescription>
      {tooLarge && <p className="text-sm text-destructive">{t('common.fileTooLarge')}</p>}
      <ErrorNote error={add.error ?? remove.error} />

      {confirmRemove && (
        <ConfirmDialog
          title={t('reminders.fileRemoveTitle')}
          description={t('common.deleteFileHint', { filename: confirmRemove.filename })}
          confirmLabel={t('common.delete')}
          busy={remove.isPending}
          error={remove.error}
          onClose={() => setConfirmRemove(null)}
          onConfirm={() =>
            remove.mutate(
              { ...ref, attachmentId: confirmRemove.id },
              { onSuccess: () => setConfirmRemove(null) },
            )
          }
        />
      )}
    </Field>
  );
}

function toLocalInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ReminderDialog({
  organization,
  reminder,
  onClose,
}: {
  organization: Organization;
  reminder?: Reminder;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(reminder?.title ?? '');
  const [note, setNote] = useState(reminder?.note ?? '');
  const [due, setDue] = useState(toLocalInput(reminder?.dueAt));
  const [repeat, setRepeat] = useState<string>(reminder?.repeat ?? ONCE);
  const [assignee, setAssignee] = useState<string>(reminder?.assignee?.userId ?? NOBODY);

  const members = useStaffMembers(organization.id);
  const create = useCreateReminder();
  const update = useUpdateReminder();
  const mutation = reminder ? update : create;
  const repeatLabels = useRepeatLabels();

  const assigneeLabels: Record<string, string> = {
    [NOBODY]: t('reminders.everyone'),
    ...Object.fromEntries(
      (members.data ?? []).map((m) => [m.userId, m.name ?? m.email]),
    ),
  };

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const dueAt = due ? new Date(due).toISOString() : null;
    const assigneeId = assignee === NOBODY ? null : assignee;
    const repeatValue = repeat === ONCE ? null : (repeat as ReminderRepeat);

    if (reminder) {
      update.mutate(
        {
          organizationId: organization.id,
          reminderId: reminder.id,
          title,
          note: note.trim() || null,
          dueAt,
          repeat: repeatValue,
          assigneeId,
        },
        { onSuccess: onClose },
      );
      return;
    }

    create.mutate(
      {
        organizationId: organization.id,
        title,
        note: note.trim() || undefined,
        dueAt: dueAt ?? undefined,
        repeat: repeatValue ?? undefined,
        assigneeId: assigneeId ?? undefined,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{reminder ? t('reminders.editTitle') : t('reminders.add')}</DialogTitle>
          <DialogDescription>{t('reminders.assigneeHint')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="reminder-title">{t('reminders.titleLabel')}</FieldLabel>
              <Input
                id="reminder-title"
                required
                autoFocus
                maxLength={120}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('reminders.titlePlaceholder')}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="reminder-note">{t('reminders.noteLabel')}</FieldLabel>
              <Input
                id="reminder-note"
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('reminders.notePlaceholder')}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="reminder-assignee">
                {t('reminders.assigneeLabel')}
              </FieldLabel>
              <Select
                items={assigneeLabels}
                value={assignee}
                onValueChange={(value) => setAssignee(String(value ?? NOBODY))}
              >
                <SelectTrigger id="reminder-assignee" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NOBODY}>{t('reminders.everyone')}</SelectItem>
                  {members.data?.map((member) => (
                    <SelectItem key={member.userId} value={member.userId}>
                      {member.name ?? member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel htmlFor="reminder-due">{t('reminders.dueLabel')}</FieldLabel>
              <Input
                id="reminder-due"
                type="datetime-local"
                required={repeat !== ONCE}
                value={due}
                onChange={(e) => setDue(e.target.value)}
              />
              <FieldDescription>
                {repeat === ONCE
                  ? t('reminders.dueHint')
                  : t('reminders.repeatNeedsDue')}
              </FieldDescription>
            </Field>

            <Field>
              <FieldLabel htmlFor="reminder-repeat">{t('reminders.repeatLabel')}</FieldLabel>
              <Select
                items={repeatLabels}
                value={repeat}
                onValueChange={(value) => setRepeat(String(value ?? ONCE))}
              >
                <SelectTrigger id="reminder-repeat" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ONCE}>{repeatLabels[ONCE]}</SelectItem>
                  {REPEATS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {repeatLabels[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldDescription>{t('reminders.repeatHint')}</FieldDescription>
            </Field>

            {reminder ? (
              <Attachments organization={organization} reminder={reminder} />
            ) : (
              <FieldDescription>{t('reminders.filesAfterCreate')}</FieldDescription>
            )}

            <ErrorNote error={mutation.error} />
          </FieldGroup>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              {t('reminders.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : reminder ? (
                <Check />
              ) : (
                <Plus />
              )}
              {mutation.isPending
                ? reminder
                  ? t('reminders.saving')
                  : t('reminders.adding')
                : reminder
                  ? t('reminders.save')
                  : t('reminders.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
